import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { UserInfo, LoginParams, LoginResponse } from '@/types/user'
import { login as loginApi, getUserInfo as getUserInfoApi, logout as logoutApi } from '@/api/auth'
import { setAccessToken, removeToken, setRefreshToken, setTenantId, getAccessToken } from '@/utils/auth'
import { deleteUserCache } from '@/hooks/web/useCache'

export const useUserStore = defineStore('user', () => {
  const userInfo = ref<UserInfo | null>(null)
  // 🔧 修复：初始化时从 localStorage 恢复 token
  const token = ref<string>(getAccessToken() || '')

  const login = async (params: LoginParams) => {
    try {
      const res = await loginApi(params)
      console.log('[UserStore] 登录响应:', res)
      
      // 后端返回的是accessToken，不是token
      if (!res.data || !res.data.accessToken) {
        throw new Error('登录响应格式错误：缺少 accessToken')
      }
      
      token.value = res.data.accessToken
      setAccessToken(res.data.accessToken)
      setRefreshToken(res.data.refreshToken)
      
      console.log('[UserStore] Token 已设置:', {
        accessToken: res.data.accessToken?.substring(0, 20) + '...',
        refreshToken: res.data.refreshToken?.substring(0, 20) + '...'
      })
      
      // 设置租户ID（从环境变量或登录响应中获取）
      const tenantId = import.meta.env.VITE_TENANT_ID || '1'
      setTenantId(Number(tenantId))
      
      // 🔧 关键修复：等待较长时间，确保后端的token已完全写入Redis/MySQL
      // 原因：后端创建token需要写入数据库和Redis，这个过程需要时间
      // 如果getUserInfo调用太快，checkAccessToken会找不到刚创建的token
      console.log('[UserStore] 等待500ms，确保后端token完全写入...')
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 登录成功后获取用户信息
      console.log('[UserStore] 开始获取用户信息...')
      try {
        await getUserInfo()
        console.log('[UserStore] 用户信息获取成功')
      } catch (getUserInfoError: any) {
        console.error('[UserStore] 获取用户信息失败，但不影响登录:', getUserInfoError)
        // 🔧 如果获取用户信息失败，不要抛出错误，让用户可以进入系统
        // 用户信息可以在后续页面中自动重试获取
      }
      
      return res
    } catch (error) {
      console.error('[UserStore] 登录失败:', error)
      // 登录失败，清理 token
      token.value = ''
      removeToken()
      throw error
    }
  }

  const getUserInfo = async () => {
    const res = await getUserInfoApi()
    userInfo.value = res.data
    return res
  }

  const logout = async () => {
    try {
      await logoutApi()
    } catch (error) {
      console.error('退出登录API调用失败:', error)
    } finally {
      // 清除本地状态
      token.value = ''
      userInfo.value = null
      // 清除token
      removeToken()
      // 清除用户相关缓存
      deleteUserCache()
      // 🔧 说明：不清除企业身份数据（portal_current_identity 等）
      // localStorage 中的身份数据仅作为页面渲染的快速缓存，
      // 重新登录后 fetchEnterpriseList() 会从后端获取真正的身份状态并覆盖
    }
  }

  return {
    userInfo,
    token,
    login,
    getUserInfo,
    logout,
  }
})
